// Navbar.jsx — Sticky navbar with MUI icons, search, wishlist, occasions dropdown

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

// MUI Icons
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import CasinoOutlinedIcon from "@mui/icons-material/CasinoOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Toys", to: "/?category=Toys" },
  { label: "Games", to: "/?category=Games" },
  { label: "Handicrafts", to: "/?category=Handicrafts" },
];

const OCCASIONS = [
  { label: "Birthday", Icon: CakeOutlinedIcon, to: "/?occasion=birthday" },
  { label: "Anniversary", Icon: FavoriteBorderIcon, to: "/?occasion=anniversary" },
  { label: "Kids", Icon: ChildCareOutlinedIcon, to: "/?occasion=kids" },
  { label: "Festival", Icon: CelebrationOutlinedIcon, to: "/?occasion=festival" },
  { label: "Games Night", Icon: CasinoOutlinedIcon, to: "/?occasion=games" },
  { label: "All Gifts", Icon: CardGiftcardOutlinedIcon, to: "/" },
];

export default function Navbar() {
  const { currentUser, isAdmin } = useAuth();
  const { cartCount } = useCart();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [wishCount, setWishCount] = useState(0);
  const [occasionOpen, setOccasionOpen] = useState(false);

  // Scroll effect
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Wishlist count from localStorage
  useEffect(() => {
    const refresh = () => {
      const stored = JSON.parse(localStorage.getItem("giftcraft_wishlist") || "[]");
      setWishCount(stored.length);
    };
    refresh();
    window.addEventListener("wishlist_updated", refresh);
    return () => window.removeEventListener("wishlist_updated", refresh);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setOccasionOpen(false);
  }, [location]);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" && !location.search
      : location.pathname + location.search === to;

  return (
    <>
      {/* ── Announcement bar ──────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-rose-600 via-purple-600 to-amber-500 text-white text-center text-xs py-2 px-4 font-medium tracking-wide overflow-hidden">
        <div className="marquee-inner inline-flex gap-12 whitespace-nowrap">
          <span>Free shipping on orders over $50</span>
          <span>·</span>
          <span>Use code <strong>SAVE10</strong> for 10% off</span>
          <span>·</span>
          <span>Same-day delivery available</span>
          <span>·</span>
          <span>New arrivals every week</span>
          <span>·</span>
          <span>Free shipping on orders over $50</span>
          <span>·</span>
          <span>Use code <strong>SAVE10</strong> for 10% off</span>
        </div>
      </div>

      {/* ── Main navbar ───────────────────────────────────────────── */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${scrolled
            ? "glass shadow-card border-b border-white/60"
            : "bg-white/95 backdrop-blur-sm shadow-sm border-b border-rose-50"
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* ── Logo ───────────────────────────────────────────── */}
            <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0">
              <motion.div
                whileHover={{ rotate: [0, -10, 10, -5, 0], scale: 1.15 }}
                transition={{ duration: 0.5 }}
                className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-gift-sm overflow-hidden"
              >
                <img src="/images/logo.jpg" alt="Generic Ideas Logo" className="w-full h-full object-cover" />
              </motion.div>
              <div className="flex flex-col leading-none">
                <span className="text-xl font-display font-bold text-gradient-gift">Generic Ideas</span>
                <span className="text-[10px] text-gray-400 font-medium tracking-widest uppercase">Online Store</span>
              </div>
            </Link>

            {/* ── Desktop nav links ──────────────────────────────── */}
            <div className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${isActive(link.to)
                      ? "text-rose-600 bg-rose-50"
                      : "text-gray-700 hover:text-rose-600 hover:bg-rose-50/60"
                    }`}
                >
                  {link.label}
                  {isActive(link.to) && (
                    <motion.div
                      layoutId="nav-indicator"
                      className="absolute bottom-0 left-3 right-3 h-0.5 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full"
                    />
                  )}
                </Link>
              ))}

              {/* Occasions dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setOccasionOpen(true)}
                onMouseLeave={() => setOccasionOpen(false)}
              >
                <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-rose-600 hover:bg-rose-50/60 rounded-lg transition-all">
                  Occasions
                  <KeyboardArrowDownIcon sx={{ fontSize: 16, transition: "transform 0.2s", transform: occasionOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                </button>

                <AnimatePresence>
                  {occasionOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.97 }}
                      transition={{ duration: 0.18 }}
                      className="absolute top-full left-0 mt-1 w-52 bg-white rounded-2xl shadow-card-hover border border-rose-50 py-2 z-50"
                    >
                      {OCCASIONS.map(({ label, Icon, to }) => (
                        <Link
                          key={label}
                          to={to}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                        >
                          <Icon sx={{ fontSize: 20, color: "inherit" }} />
                          <span className="font-medium">{label}</span>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* ── Right actions ──────────────────────────────────── */}
            <div className="flex items-center gap-1">

              {/* Wishlist */}
              <Link
                to="/wishlist"
                className="relative p-2.5 text-gray-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                aria-label="Wishlist"
              >
                {wishCount > 0
                  ? <FavoriteIcon sx={{ fontSize: 20, color: "#f0155a" }} />
                  : <FavoriteBorderOutlinedIcon sx={{ fontSize: 20 }} />
                }
                {wishCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                    className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full font-bold"
                  >
                    {wishCount}
                  </motion.span>
                )}
              </Link>

              {/* Cart */}
              <Link
                to="/cart"
                className="relative p-2.5 text-gray-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                aria-label="Cart"
              >
                <ShoppingCartOutlinedIcon sx={{ fontSize: 20 }} />
                <AnimatePresence>
                  {cartCount > 0 && (
                    <motion.span
                      key={cartCount}
                      initial={{ scale: 0, y: -4 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0 }}
                      className="absolute -top-0.5 -right-0.5 bg-gradient-to-br from-rose-500 to-pink-600 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full font-bold shadow-gift-sm"
                    >
                      {cartCount > 99 ? "99+" : cartCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>

              {/* Auth — desktop */}
              {currentUser ? (
                <div className="hidden md:flex items-center gap-2 ml-1">
                  {isAdmin ? (
                    <Link
                      to="/admin"
                      className="flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-3 py-1.5 rounded-xl hover:bg-purple-100 transition-colors"
                    >
                      <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 15 }} /> Admin
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/profile"
                        className="relative p-2.5 text-gray-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                        aria-label="Profile"
                      >
                        <PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                      </Link>
                      <Link to="/orders" className="text-xs text-gray-600 hover:text-rose-600 transition-colors px-3 py-1.5 rounded-xl hover:bg-rose-50">
                        My Orders
                      </Link>
                    </>
                  )}
                </div>
              ) : (
                <div className="hidden md:flex items-center gap-2 ml-1">
                  <Link to="/login" className="text-xs font-medium text-gray-700 hover:text-rose-600 transition-colors px-3 py-1.5 rounded-xl hover:bg-rose-50">
                    Login
                  </Link>
                  <Link to="/signup" className="text-xs font-semibold bg-gradient-to-r from-rose-500 to-pink-600 text-white px-4 py-1.5 rounded-xl hover:opacity-90 transition-opacity shadow-gift-sm">
                    Sign Up
                  </Link>
                </div>
              )}

              {/* Mobile hamburger */}
              <motion.button
                whileTap={{ scale: 0.9 }}
                className="md:hidden p-2.5 text-gray-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                {menuOpen
                  ? <CloseIcon sx={{ fontSize: 20 }} />
                  : <MenuIcon sx={{ fontSize: 20 }} />
                }
              </motion.button>
            </div>
          </div>
        </div>

        {/* ── Mobile menu ───────────────────────────────────────── */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden border-t border-rose-50 bg-white/95 backdrop-blur-sm overflow-hidden"
            >
              <div className="px-4 py-4 space-y-1">
                {NAV_LINKS.map((link, i) => (
                  <motion.div key={link.label} initial={{ x: -16, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.05 }}>
                    <Link to={link.to} onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all">
                      {link.label}
                    </Link>
                  </motion.div>
                ))}

                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest px-3 mb-2">Occasions</p>
                  {OCCASIONS.map(({ label, Icon, to }, i) => (
                    <motion.div key={label} initial={{ x: -16, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: (NAV_LINKS.length + i) * 0.05 }}>
                      <Link to={to} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all">
                        <Icon sx={{ fontSize: 18 }} /> {label}
                      </Link>
                    </motion.div>
                  ))}
                </div>

                <div className="pt-3 border-t border-gray-100 space-y-2">
                  {currentUser ? (
                    <>
                      {isAdmin
                        ? <Link to="/admin" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 text-sm font-semibold text-purple-700 hover:bg-purple-50 rounded-xl">Admin Dashboard</Link>
                        : (
                          <>
                            <Link to="/profile" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 text-sm text-gray-700 hover:bg-rose-50 hover:text-rose-600 rounded-xl">Profile</Link>
                            <Link to="/orders" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 text-sm text-gray-700 hover:bg-rose-50 hover:text-rose-600 rounded-xl">My Orders</Link>
                          </>
                        )
                      }
                    </>
                  ) : (
                    <div className="flex gap-2">
                      <Link to="/login" onClick={() => setMenuOpen(false)} className="flex-1 text-center py-2.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-xl hover:border-rose-300 hover:text-rose-600 transition">Login</Link>
                      <Link to="/signup" onClick={() => setMenuOpen(false)} className="flex-1 text-center py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl hover:opacity-90 transition shadow-gift-sm">Sign Up</Link>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
}
